import { useEffect, useState } from 'react';
import { X, Download, Trash2, ZoomIn, ZoomOut } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Document {
  id: string;
  file_name: string;
  file_type: 'pdf' | 'image';
  storage_path: string;
  document_type: string;
  description: string | null;
}

interface DocumentPreviewProps {
  document: Document;
  onClose: () => void;
  onDownload: () => void;
  onDelete: () => void;
}

export function DocumentPreview({ document, onClose, onDownload, onDelete }: DocumentPreviewProps) {
  const [fileUrl, setFileUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    loadFile();

    // Close on ESC key
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [document]);

  async function loadFile() {
    try {
      const { data, error } = await supabase.storage
        .from('maintenance-documents')
        .createSignedUrl(document.storage_path, 3600); // 1 hour expiry

      if (error) throw error;
      setFileUrl(data.signedUrl);
    } catch (error) {
      console.error('Error loading file:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 truncate">
              {document.file_name}
            </h3>
            <div className="flex items-center gap-3 mt-1">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                {document.document_type}
              </span>
              {document.description && (
                <span className="text-sm text-slate-600">{document.description}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4">
            {/* Zoom Controls for Images */}
            {document.file_type === 'image' && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-5 h-5 text-slate-600" />
                </button>
                <span className="text-sm text-slate-600 min-w-[4rem] text-center">
                  {zoom}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="w-5 h-5 text-slate-600" />
                </button>
                <div className="w-px h-6 bg-slate-300 mx-2"></div>
              </>
            )}

            <button
              onClick={onDownload}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5 text-slate-600" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="w-5 h-5 text-red-600" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Close (ESC)"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-slate-50 p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {document.file_type === 'pdf' ? (
                <iframe
                  src={fileUrl}
                  className="w-full h-full min-h-[600px] rounded-lg border border-slate-300"
                  title={document.file_name}
                />
              ) : (
                <div className="flex items-center justify-center min-h-[600px]">
                  <img
                    src={fileUrl}
                    alt={document.file_name}
                    className="max-w-full h-auto rounded-lg shadow-lg"
                    style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'center' }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 text-center text-sm text-slate-600">
          Press <kbd className="px-2 py-1 bg-white border border-slate-300 rounded text-xs">ESC</kbd> to close
        </div>
      </div>
    </div>
  );
}
